import { isString, omitBy, reduce } from 'lodash';
import { uniq } from 'lodash/array';
import { isEmpty } from 'lodash/lang';
import moment from 'moment';

import { normalizePhoneNumber, serializePhoneNumber, validatePhoneNumber } from '../../../../services/geo/e-164';
import Geo, { countries, getPostalCodeRegex } from '../../../../services/geo/index';
import { caseInsensitiveStringEquals } from '../../../../services/string';

export async function validateAndFormatPatientData(patientData, notUsInstance) {
  const validatedPatients = await Promise.all(
    patientData.map(patient => validateAndFormatPatient(patient, notUsInstance))
  );
  return reduce(
    validatedPatients,
    (acc, { row, issues, updatedPatient }) => {
      if (!isEmpty(issues)) {
        acc.issues.set(row, issues);
      }
      acc.patientData.push(updatedPatient);
      return acc;
    },
    { issues: new Map(), patientData: [] }
  );
}

async function validateAndFormatPatient(patientData, notUsInstance) {
  const {
    row,
    firstName,
    lastName,
    dob,
    country,
    state,
    email,
    dialCode,
    phone,
    phoneType,
    zip,
    inclinationScore,
    weight,
    height,
    pronouns,
    ethnicities,
    races,
    sex,
    preferredContactMethod,
    primaryLanguage,
    optIn
  } = patientData;

  const updatedPatient = { ...patientData };
  const issues = [];

  const szdFirstName = sanitize(firstName);
  const szdLastName = sanitize(lastName);
  if (szdFirstName && szdLastName) {
    updatedPatient.firstName = szdFirstName;
    updatedPatient.lastName = szdLastName;
  } else {
    issues.push('Both first and last name are required');
  }

  const szdDob = sanitize(dob);
  if (szdDob) {
    const parsedDate = moment(szdDob, 'M/D/YYYY', true);
    if (parsedDate.isValid()) {
      const earliestValidDate = moment().subtract(110, 'years');
      const latestValidDate = moment();
      if (parsedDate.isBefore(latestValidDate) && parsedDate.isAfter(earliestValidDate)) {
        updatedPatient.dob = parsedDate.format('YYYY-MM-DD');
      } else {
        issues.push('Date of birth is not within the valid range');
      }
    } else {
      issues.push('Date of birth is incorrect. Use the format: M/D/YYYY');
    }
  } else {
    issues.push('Missing date of birth');
  }

  const szdCountry = sanitize(country);
  let countryInfo = searchByIdAndName(szdCountry, countries);
  if (!countryInfo) {
    if (notUsInstance) {
      issues.push('Country is missing or not recognized');
    } else {
      countryInfo = searchByIdAndName('US', countries);
    }
  }
  updatedPatient.country = countryInfo?.id;

  const szdState = sanitize(state);
  let stateId = null;
  if (szdState) {
    if (!countryInfo) {
      issues.push('Cannot add state without country');
    } else {
      const regions = await Geo.getRegions(countryInfo.id);
      stateId = searchByIdAndLocalName(szdState, regions)?.id;
    }
  }
  updatedPatient.state = stateId;

  const szdEmail = sanitize(email);
  const szdPhone = sanitize(phone);
  if (!email && !phone) {
    issues.push('Either phone or email is required');
  }

  let normalizedEmail = null;
  if (szdEmail) {
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (emailPattern.test(szdEmail)) {
      normalizedEmail = szdEmail;
    } else {
      issues.push('Email is not valid');
    }
  }
  updatedPatient.email = normalizedEmail;

  let normalizedPhone = null;
  let phoneCountryInfo = null;
  if (szdPhone) {
    phoneCountryInfo = getCountryByDialCode(dialCode, countryInfo);
    if (!phoneCountryInfo) {
      issues.push('Dial code is missing or not recognized');
    } else {
      const phoneResult = normalizePhone(szdPhone, phoneCountryInfo);
      if (!phoneResult.error) {
        normalizedPhone = phoneResult;
      } else {
        issues.push(phoneResult.error);
      }
    }
  }
  updatedPatient.phoneCountry = phoneCountryInfo?.id;
  updatedPatient.phone = normalizedPhone;

  const szdZip = sanitize(zip);
  let normalizedZip = null;
  if (szdZip && !countryInfo) {
    issues.push('Cannot add zip without country');
  } else {
    normalizedZip = getPostalCodeRegex(countryInfo.id).test(szdZip) ? szdZip : null;
  }
  updatedPatient.zip = normalizedZip;

  updatedPatient.phoneType = normalizedPhone ? normalizeEntity(phoneType, normalizePhoneType, null) : null;
  updatedPatient.inclinationScore = normalizeInclinationScore(inclinationScore, normalizeInclinationScore, null);
  updatedPatient.height = normalizeEntity(height, normalizeHeight, null);
  updatedPatient.weight = normalizeEntity(weight, normalizeWeight, null);
  updatedPatient.pronouns = normalizeEntity(pronouns, normalizePronouns, null);
  updatedPatient.ethnicities = normalizeEntity(ethnicities, normalizeEthnicities, []);
  updatedPatient.races = normalizeEntity(races, normalizeRaces, []);
  updatedPatient.sex = normalizeEntity(sex, normalizeSex, null);
  updatedPatient.preferredContactMethod = normalizeEntity(preferredContactMethod, normalizePreferredContact, null);
  updatedPatient.primaryLanguage = normalizeEntity(primaryLanguage, normalizePrimaryLanguage, null);
  updatedPatient.optIn = normalizeEntity(optIn, normalizeOptIn, null);

  const cleanedPatient = omitBy(
    updatedPatient,
    value => value === null || (Array.isArray(value) && value.length === 0)
  );

  return {
    row,
    issues,
    updatedPatient: cleanedPatient
  };
}

function sanitize(value) {
  return isString(value) ? value.trim() : value;
}

function searchByIdAndName(countryName, countries) {
  return countries.find(
    country =>
      caseInsensitiveStringEquals(country.id, countryName) || caseInsensitiveStringEquals(country.name, countryName)
  );
}

function searchByIdAndLocalName(regionName, regions) {
  return regions.find(
    region =>
      caseInsensitiveStringEquals(region.id, regionName) || caseInsensitiveStringEquals(region.localName, regionName)
  );
}

/*
   Dial code '1' requires additional handling because it is shared by multiple countries (e.g. US, Canada, some Caribbean countries).
   Logic to determine the correct country for dial code '1' depends on the country of the patient:
    1. If the patient country has a dial code of '1', we will use the patient country as the phone country.
    2. Otherwise, we will default to US for dial code '1' since it is the most common country with that dial code.
 */
function getCountryByDialCode(dialCode, patientCountryInfo) {
  const szdDialCode = sanitize(dialCode);
  if (szdDialCode === '1') {
    if (!patientCountryInfo) {
      return null;
    }
    return patientCountryInfo.dialCode === '1' ? patientCountryInfo : searchByIdAndName('US', countries);
  } else {
    return countries.find(c => caseInsensitiveStringEquals(c.dialCode, szdDialCode)) || patientCountryInfo;
  }
}

/*
   Phone number input is accepted both with and without dial code of the country. Therefore,
   we need to account for both cases. We try to determine if the phone starts with the dial code.
   If it is, we remove it and try to validate the phone without it. However, there can be a problem if
   phone number starts with the same digits as the dial code but doesn't actually include the dial code.
   For this specific case, we will perform an additional validation of the phone number with the dial code included.
   During additional validation, dial code will be treated as part of the phone number.
*/
function normalizePhone(phone, country) {
  const phoneWithDialCode = serializePhoneNumber(phone, country.id);
  if (phoneWithDialCode.startsWith(country.dialCode)) {
    const phoneWithoutDialCode = phoneWithDialCode.substring(country.dialCode.length);
    const phoneWithoutDialCodeError = validatePhoneNumber(country.id, phoneWithoutDialCode);
    if (!phoneWithoutDialCodeError) {
      return normalizePhoneNumber(phoneWithoutDialCode, country.id);
    }
  }

  const phoneWithDialCodeError = validatePhoneNumber(country.id, phoneWithDialCode);
  if (!phoneWithDialCodeError) {
    return normalizePhoneNumber(phoneWithDialCode, country.id);
  }

  return { error: 'Phone number is not valid' };
}

function normalizeEntity(input, normalizer, defaultValue) {
  const szdInput = sanitize(input);
  return szdInput ? normalizer(szdInput) : defaultValue;
}

function normalizeInclinationScore(inclinationScoreInput) {
  const parserInclinationScore = parseInt(inclinationScoreInput, 10);
  return isNaN(parserInclinationScore) ? null : parserInclinationScore;
}

function normalizeHeight(heightInput) {
  const parsedHeight = parseInt(heightInput, 10);
  if (isNaN(parsedHeight) || parsedHeight < 24 || parsedHeight > 107) {
    return null;
  } else {
    return parsedHeight;
  }
}

function normalizeWeight(weightInput) {
  const parsedWeight = parseInt(weightInput, 10);
  if (isNaN(parsedWeight) || parsedWeight > 999) {
    return null;
  } else {
    return parsedWeight;
  }
}

function normalizeSingularEntry(data, mapping) {
  const key = Object.keys(mapping).find(
    key => caseInsensitiveStringEquals(key, data) || caseInsensitiveStringEquals(mapping[key], data)
  );
  return key ? mapping[key] : null;
}

function normalizePluralEntries(data, mapping) {
  const result = data
    .split('|')
    .map(sanitize)
    .map(item => normalizeSingularEntry(item, mapping))
    .filter(item => item !== null);
  return uniq(result);
}

function normalizePronouns(pronounsInput) {
  const pronounsMapping = {
    'she/her': 'SHE',
    'he/him': 'HE',
    'they/them': 'THEY'
  };
  const key = Object.keys(pronounsMapping).find(key => caseInsensitiveStringEquals(key, pronounsInput));
  return key ? pronounsMapping[key] : null;
}

function normalizeEthnicities(ethnicitiesInput) {
  const ethnicityMapping = {
    'Not Hispanic, Latino/a, or Spanish origin': 'NOT_HISPANIC',
    'Mexican, Mexican American, Chicano/a': 'MEXICAN',
    'Puerto Rican': 'PUERTO_RICAN',
    Cuban: 'CUBAN',
    'Another Hispanic, Latino/a or Spanish origin': 'OTHER_HISPANIC'
  };
  return normalizePluralEntries(ethnicitiesInput, ethnicityMapping);
}

function normalizeRaces(raceInput) {
  const raceMapping = {
    White: 'WHITE',
    'Black or African American': 'BLACK',
    'American Indian or Alaska Native': 'AMERICAN_INDIAN',
    'Asian Indian': 'ASIAN_INDIAN',
    Chinese: 'CHINESE',
    Filipino: 'FILIPINO',
    Japanese: 'JAPANESE',
    Korean: 'KOREAN',
    Vietnamese: 'VIETNAMESE',
    'Other Asian': 'OTHER_ASIAN',
    'Native Hawaiian': 'HAWAIIAN',
    'Guamanian or Chamarro': 'GUAMANIAN',
    Samoan: 'SAMOAN',
    'Other Pacific Islander': 'OTHER_PACIFIC'
  };
  return normalizePluralEntries(raceInput, raceMapping);
}

function normalizeSex(input) {
  const sexMapping = {
    F: 'FEMALE',
    M: 'MALE'
  };
  return normalizeSingularEntry(input, sexMapping);
}

function normalizePhoneType(input) {
  const phoneTypeMapping = {
    Mobile: 'M',
    Landline: 'L'
  };
  return normalizeSingularEntry(input, phoneTypeMapping);
}

function normalizePreferredContact(input) {
  const preferredContactMapping = {
    Phone: 'PH',
    SMS: 'SMS',
    Email: 'EM'
  };
  return normalizeSingularEntry(input, preferredContactMapping);
}

function normalizePrimaryLanguage(input) {
  const primaryLanguageMapping = {
    English: 'EN',
    Spanish: 'ES',
    Mandarin: 'MD',
    Cantonese: 'CA',
    Tagalog: 'TL',
    Arabic: 'AR',
    Vietnamese: 'VI',
    Korean: 'KO',
    Bulgarian: 'BG',
    Croatian: 'HR',
    Czech: 'CS',
    Danish: 'DA',
    Dutch: 'NL',
    French: 'FR',
    German: 'DE',
    Greek: 'EL',
    Hebrew: 'HE',
    Hindi: 'HI',
    Hungarian: 'HU',
    Italian: 'IT',
    Norwegian: 'NO',
    Polish: 'PL',
    Portuguese: 'PT',
    Romanian: 'RO',
    Swedish: 'SV',
    Turkish: 'TR',
    Ukrainian: 'UK',
    Other: 'OT'
  };
  return normalizeSingularEntry(input, primaryLanguageMapping);
}

function normalizeOptIn(input) {
  const optInMapping = {
    yes: 'YES',
    y: 'YES',
    no: 'NO',
    n: 'NO'
  };
  return normalizeSingularEntry(input, optInMapping);
}
